import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

/**
 * The 28px muted icon-button treatment, as a descendant rule.
 *
 * Applied from a wrapper rather than from muiTheme.ts because MRT's toolbars are
 * bare `Box` elements with no themeable slot — `muiTopToolbarProps` is the only
 * hook. Reaching the buttons through a wrapper also lands the rules at
 * two-class specificity, so they outrank both MRT's own per-button `sx` and any
 * single-class `sx` left on an individual button.
 */
const iconButtons = (theme: Theme) => ({
  '& .MuiIconButton-root': {
    width: 28,
    height: 28,
    borderRadius: '6px',
    color: theme.palette.cx.text.tertiary,
    '& .MuiSvgIcon-root': { fontSize: 16 },
    '&:hover': {
      backgroundColor: theme.palette.cx.bg.elevated,
      color: theme.palette.cx.text.primary,
    },
    '&.Mui-disabled': {
      color: theme.palette.cx.text.muted,
      // Beats the hardcoded `rgba(0, 0, 0, ...)` disabled colors that predate
      // the dark theme, which were effectively invisible on a dark row.
      opacity: 0.45,
    },
  },

  // Escape hatch for a genuinely destructive action: pass the standard
  // `color="error"` at the call site and the icon stays red instead of muted.
  //
  // Written as a separate rule rather than folded above because it needs to
  // outrank the muted default. With the wrapper that is three classes against
  // two, so it wins on specificity alone — no reliance on the order emotion
  // happens to inject the two classes in.
  '& .MuiIconButton-root.MuiIconButton-colorError': {
    color: theme.palette.cx.red.main,
    '&:hover': {
      color: theme.palette.cx.red.main,
      backgroundColor: alpha(theme.palette.cx.red.main, 0.12),
    },
    // Restated at four classes so a disabled destructive button goes grey like
    // any other. The `.Mui-disabled` rule above ties with this block's selector
    // on specificity, and a tie would be settled by injection order.
    '&.Mui-disabled': {
      color: theme.palette.cx.text.muted,
    },
  },
});

/**
 * Wrapper for a MaterialReactTable `renderRowActions` group.
 *
 * Deliberately typed as the theme-callback form rather than the full
 * `SxProps<Theme>` union. `SxProps` already includes an array member, so a value
 * of that type cannot be nested inside an `sx` array — which is exactly what a
 * call site needs in order to add its own rules, e.g.
 * `sx={[ROW_ACTIONS_SX, { justifyContent: 'flex-end' }]}`.
 */
export const ROW_ACTIONS_SX = (theme: Theme) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: '2px',
  ...iconButtons(theme),
});

/**
 * For `muiTopToolbarProps`, so the search / filter / column / density /
 * fullscreen buttons match the per-row actions.
 *
 * Only the icon rules — no layout. MRT owns the toolbar's own flex layout
 * (custom actions on the left, internal buttons on the right), and overriding
 * that here would break it.
 */
export const TOOLBAR_ACTIONS_SX = (theme: Theme) => ({
  ...iconButtons(theme),
});
